import { FileInputValue } from '../../../hooks/UseFileInput';
import { buildUrl, Endpoint, httpStatusCodes, tartleClient } from '../../../lib/api';
import { ErrorTags, logger } from '../../../lib/logger';
import { Attachment } from '../../../types/sellersPacket.schema';
import { compact } from '../../../utils';
import { ProgressMessage } from './UploadProgressModal';

// Gather checksums and request an upload url for each file.
export const createAttachments = async ({
  createAttachmentEndpoint,
  createAttachmentEndpointParams,
  files,
  updateProgress = () => null
}: {
  createAttachmentEndpoint: Endpoint;
  createAttachmentEndpointParams: string[];
  files: FileInputValue[];
  updateProgress: (progress: ProgressMessage) => void;
}) => {
  updateProgress({ message: 'Preparing files...', percentFinished: 0 });

  return await Promise.all(
    files.map(({ file, mime, checksum }) =>
      tartleClient<Attachment>({
        method: 'POST',
        endpoint: createAttachmentEndpoint,
        endpointParams: createAttachmentEndpointParams,
        data: {
          filename: file.name,
          byteSize: file.size.toString(),
          contentType: mime,
          checksum
        }
      })
    )
  );
};

// Uploads each file to AWS via an upload url provided by the server.
export const awsUpload = async ({
  createAttachmentEndpoint,
  createAttachmentEndpointParams = [],
  files,
  // You can use this without a UI by not passing the last 2 props.
  toggleUploadProgressModal = () => null,
  updateProgress = () => null
}: {
  createAttachmentEndpoint: Endpoint;
  createAttachmentEndpointParams?: string[];
  toggleUploadProgressModal?: () => void;
  updateProgress?: (progress: ProgressMessage) => void;
  files: FileInputValue[];
}): Promise<Attachment[]> => {
  const log = logger('fileupload');
  toggleUploadProgressModal();
  try {
    log.groupCollapsed(`Uploading ${files.length} files`);
    const attachments = await createAttachments({
      createAttachmentEndpoint,
      createAttachmentEndpointParams,
      files,
      updateProgress
    });
    log.info('Attachments created', { attachments });

    updateProgress({
      message: `Preparing file${files.length > 1 ? 's' : ''} ...`,
      percentFinished: 100
    });

    for (let i = 0; i < attachments.length; i++) {
      const message = `Uploading ${compact(
        files[i].file?.name.toLowerCase() || `file ${i}`
      )}`;

      updateProgress({ message, percentFinished: 0 });

      const result = await uploadFile(
        files[i].file,
        attachments[i].directUpload.url,
        attachments[i].directUpload.headers,
        message,
        updateProgress
      );
      log.info(`File ${i} uploaded`, { result });
    }
    toggleUploadProgressModal();
    log.groupEnd();

    return attachments;
  } catch (error: unknown) {
    const tags: ErrorTags = {
      attachmentEndpoint: buildUrl(
        createAttachmentEndpoint[0],
        createAttachmentEndpointParams
      )
    };
    if (isUploadError(error)) {
      tags['status'] = error.status;
      if (error.response !== 'Network error') {
        log.exception({
          error: `${error.response} (${error.status}): ${error.progress.loaded} out of ${error.progress.total} bytes uploaded`,
          errorType: httpStatusCodes[error.status] || 'File Upload Error',
          tags: { ...error.progress, ...tags }
        });
      }
    } else {
      log.exception({ error, tags });
    }

    updateProgress({
      message: 'There was an error uploading a file',
      percentFinished: 100
    });
    log.groupEnd();
    throw error;
  }
};

type UploadResult = {
  status: number;
  response: unknown;
};

type UploadError = {
  status: number;
  response: string;
  progress: {
    total: number;
    loaded: number;
  };
};

// upload a file object to a url using XMLHttpRequest to get progress events.
const uploadFile = (
  file: File,
  url: string,
  headers: Record<string, string>,
  message: string,
  updateProgress: (progress: ProgressMessage) => void
): Promise<UploadResult> =>
  new Promise((resolve, reject: (reason: UploadError) => void) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    Object.entries(headers).forEach(([key, value]) =>
      request.setRequestHeader(key, value)
    );

    const handleProgress = (e: ProgressEvent) => {
      const size = e.total ? e.total : file.size;
      updateProgress({
        message,
        percentFinished: Math.floor((e.loaded / size) * 100)
      });
    };

    const handleLoadEnd = (e: ProgressEvent) => {
      const result = {
        status: request.status,
        response: request.response as string
      };
      if (request.status >= 200 && request.status < 300) {
        logger('fileupload').info(`Uploaded ${file.name}`);
        resolve(result);
      } else {
        logger('fileupload').error(`Upload failed ${file.name}`);
        reject({
          ...result,
          progress: { total: e.total, loaded: e.loaded }
        });
      }
    };

    const handleError = (errorType: string) => (e: ProgressEvent) => {
      reject({
        status: request.status,
        response: errorType,
        progress: { total: e.total, loaded: e.loaded }
      });
    };

    request.upload.addEventListener('progress', handleProgress);
    request.addEventListener('loadend', handleLoadEnd);
    request.addEventListener('error', handleError('Network error'));
    request.addEventListener('abort', handleError('Upload aborted'));
    request.addEventListener('timeout', handleError('Upload timed out'));

    logger('fileupload').info(`Uploading ${file.name}`, {
      file,
      url,
      headers,
      message
    });
    request.send(file);
  });

const isUploadError = (result: unknown): result is UploadError =>
  typeof result === 'object' &&
  result !== null &&
  'status' in result &&
  'response' in result &&
  'progress' in result;
